import type { Sim } from "../../sim/sim";
import type { Camera } from "../camera";
import type { RenderView } from "../view";
import { FxSystem } from "../fx";
import { drawIcon } from "../art";
import { getFaction, type Faction } from "../../data";
import { KIND_UNIT, KIND_BUILDING, OWNER_PLAYER } from "../../sim/constants";
import { F_UNDER_CONSTRUCTION, ACT_BUILD, ACT_RECLAIM, ACT_REPAIR } from "../../sim/world";
import { canPlace } from "../../sim/placement";

// Screen-space tactical overlay for the PlayCanvas renderer. The 3D scene draws
// terrain / units / buildings / projectiles / explosion light; this transparent
// 2D canvas (stacked above the WebGL canvas, pointer-events:none) draws the
// crisp tactical layer that must stay pixel-locked to the DOM HUD: strategic
// icons (icon-zoom pass), selection & hover rings, health, construction arcs,
// nanolathe beams, rally lines, metal-spot glow, placement ghost, drag-box,
// order markers / explosions / tracers (reused FxSystem) and the vignette.
//
// It projects EVERY point through the same 3D Camera as the GPU (perspective +
// tilt), so all annotations sit exactly on what is drawn. Perspective keeps
// straight world lines straight, so grid/rally lines project cleanly.

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

export class Overlay {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  private sim: Sim;
  private cam: Camera;
  private dpr = 1;
  private colPlayer: string;
  private colEnemy: string;

  constructor(sim: Sim, cam: Camera, playerFaction: Faction, enemyFaction: Faction) {
    this.sim = sim;
    this.cam = cam;
    this.canvas = document.createElement("canvas");
    this.canvas.style.pointerEvents = "none";
    this.ctx = this.canvas.getContext("2d")!;
    this.colPlayer = getFaction(playerFaction).color;
    this.colEnemy = enemyFaction === playerFaction ? "#ff4dd2" : getFaction(enemyFaction).color;
  }

  private ownerColor(owner: number): string {
    return owner === OWNER_PLAYER ? this.colPlayer : owner === 2 ? this.colEnemy : "#8892a0";
  }

  resize(dpr: number, wCss: number, hCss: number): void {
    this.dpr = dpr;
    this.canvas.width = Math.max(1, Math.floor(wCss * dpr));
    this.canvas.height = Math.max(1, Math.floor(hCss * dpr));
    this.canvas.style.width = wCss + "px";
    this.canvas.style.height = hCss + "px";
  }

  draw(alpha: number, view: RenderView, fx: FxSystem): void {
    const ctx = this.ctx;
    const cam = this.cam;
    const dpr = this.dpr;
    const shx = fx.shakeX;
    const shy = fx.shakeY;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, dpr * shx, dpr * shy);

    fx.drawGround(ctx, cam);
    this.drawMetalSpots(ctx, view);
    if (view.placing) this.drawBuildGrid(ctx, cam);

    const iconZoom = cam.isIconZoom();
    const v = cam.viewRect();
    const pad = 60;
    const w = this.sim.world;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      const kind = w.kind[e];
      if (kind !== KIND_UNIT && kind !== KIND_BUILDING) continue;
      const ix = kind === KIND_UNIT ? lerp(w.prevX[e], w.posX[e], alpha) : w.posX[e];
      const iy = kind === KIND_UNIT ? lerp(w.prevY[e], w.posY[e], alpha) : w.posY[e];
      if (ix < v.x0 - pad || ix > v.x1 + pad || iy < v.y0 - pad || iy > v.y1 + pad) continue;
      this.drawAnnot(e, ix, iy, iconZoom, view);
    }

    this.drawNanolathe(ctx, alpha);
    this.drawRallyLines(ctx, view);
    fx.draw(ctx, cam);
    if (view.placing) this.drawPlacement(ctx, view.placing);
    if (view.selBox) this.drawSelBox(ctx, view.selBox);

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawVignette(ctx);
  }

  private drawAnnot(e: number, ix: number, iy: number, iconZoom: boolean, view: RenderView): void {
    const ctx = this.ctx;
    const cam = this.cam;
    const w = this.sim.world;
    const def = w.def(e);
    const p = cam.project(ix, iy);
    if (p.behind) return;
    const sx = p.x, sy = p.y;
    const z = cam.zoom;
    const owner = w.owner[e];
    const selected = view.selection.has(e);
    const uc = (w.flags[e] & F_UNDER_CONSTRUCTION) !== 0;
    const healthFrac = Math.max(0, Math.min(1, w.health[e] / w.maxHealth[e]));
    const t = performance.now() * 0.001;

    if (selected) {
      const pr = (def.radius + 4) * z + Math.sin(t * 4) * 1.2;
      ctx.strokeStyle = "#b6ffce";
      ctx.globalAlpha = 0.85 + 0.15 * Math.sin(t * 4);
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(sx, sy, pr, 0, 7); ctx.stroke();
      ctx.globalAlpha = 1;
    } else if (view.hovered === e) {
      ctx.strokeStyle = "rgba(200,220,255,0.5)";
      ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(sx, sy, (def.radius + 4) * z, 0, 7); ctx.stroke();
    }

    if (iconZoom && !uc) {
      const s = Math.max(4, (def.radius + (def.role === "commander" ? 6 : 2)) * 0.9);
      ctx.save();
      ctx.translate(sx, sy);
      drawIcon(ctx, def.role, s, this.ownerColor(owner));
      ctx.restore();
      if (healthFrac < 1) {
        ctx.strokeStyle = healthFrac > 0.5 ? "#5bd66a" : healthFrac > 0.25 ? "#e0c840" : "#e5533b";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx, sy, s + 3, -Math.PI / 2, -Math.PI / 2 + healthFrac * Math.PI * 2);
        ctx.stroke();
      }
      return;
    }

    // Player-colour identification ring at the foot (over the 3D model).
    ctx.strokeStyle = this.ownerColor(owner);
    ctx.globalAlpha = 0.34;
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(sx, sy, (def.radius + 1) * z, 0, 7); ctx.stroke();
    ctx.globalAlpha = 1;

    if (w.fireFlash[e] > 0 && def.weapon) {
      const heading = w.heading[e];
      const bx = sx + Math.cos(heading) * (def.radius + 3) * z;
      const by = sy + Math.sin(heading) * (def.radius + 3) * z;
      const fa = w.fireFlash[e] / 0.09;
      const fr = Math.max(2.5, def.radius * 0.5 * z);
      ctx.globalAlpha = fa * 0.5;
      ctx.fillStyle = "#ffcf78";
      ctx.beginPath(); ctx.arc(bx, by, fr * 1.7, 0, 7); ctx.fill();
      ctx.globalAlpha = fa;
      ctx.fillStyle = "#fff8d6";
      ctx.beginPath(); ctx.arc(bx, by, fr * 0.7, 0, 7); ctx.fill();
      ctx.globalAlpha = 1;
    }

    if (uc) {
      ctx.strokeStyle = "#7fd4ff";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.arc(sx, sy, (def.radius + 4) * z, -Math.PI / 2, -Math.PI / 2 + w.buildProgress[e] * Math.PI * 2);
      ctx.stroke();
    }

    if ((healthFrac < 1 || selected) && !uc) {
      const bw = (def.radius * 2.2) * z;
      const bh = Math.max(2.5, 3 * z * 0.6);
      const bx = sx - bw / 2;
      const by = sy - (def.radius + 10) * z; // float above the projected foot
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillRect(bx - 1, by - 1, bw + 2, bh + 2);
      ctx.fillStyle = healthFrac > 0.5 ? "#5bd66a" : healthFrac > 0.25 ? "#e0c840" : "#e5533b";
      ctx.fillRect(bx, by, bw * healthFrac, bh);
    }
  }

  private drawMetalSpots(ctx: CanvasRenderingContext2D, view: RenderView): void {
    const cam = this.cam;
    const w = this.sim.world;
    const spots = w.gmap.def.metalSpots;
    const t = performance.now() * 0.002;
    for (let i = 0; i < spots.length; i++) {
      const p = cam.project(spots[i].x, spots[i].y);
      if (p.behind) continue;
      const sx = p.x, sy = p.y;
      const occupied = w.metalSpotOccupant[i] >= 0;
      const r = 8 * Math.max(0.8, Math.min(1.7, cam.zoom));
      if (occupied) {
        ctx.strokeStyle = "rgba(150,160,170,0.4)";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(sx, sy - r); ctx.lineTo(sx + r, sy); ctx.lineTo(sx, sy + r); ctx.lineTo(sx - r, sy); ctx.closePath();
        ctx.stroke();
        continue;
      }
      const pulse = 0.6 + 0.4 * Math.sin(t + i);
      const grd = ctx.createRadialGradient(sx, sy, 0, sx, sy, r * 1.6);
      grd.addColorStop(0, `rgba(255,205,90,${0.32 * pulse})`);
      grd.addColorStop(0.6, `rgba(255,180,60,${0.14 * pulse})`);
      grd.addColorStop(1, "rgba(255,180,60,0)");
      ctx.fillStyle = grd;
      ctx.beginPath(); ctx.arc(sx, sy, r * 1.6, 0, 7); ctx.fill();
      ctx.strokeStyle = `rgba(255,215,110,${0.7 + 0.3 * pulse})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx, sy - r); ctx.lineTo(sx + r, sy); ctx.lineTo(sx, sy + r); ctx.lineTo(sx - r, sy); ctx.closePath();
      ctx.stroke();
      ctx.fillStyle = `rgba(255,235,170,${0.5 * pulse})`;
      ctx.beginPath(); ctx.arc(sx, sy, r * 0.28, 0, 7); ctx.fill();
      if (view.placing) {
        ctx.strokeStyle = "rgba(255,215,110,0.9)";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(sx, sy, r * 1.9, 0, 7); ctx.stroke();
      }
    }
  }

  private drawBuildGrid(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const step = 24;
    const v = cam.viewRect();
    ctx.strokeStyle = "rgba(120,200,235,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    const x0 = Math.floor(v.x0 / step) * step;
    for (let x = x0; x <= v.x1; x += step) {
      const a = cam.project(x, v.y0), b = cam.project(x, v.y1);
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    }
    const y0 = Math.floor(v.y0 / step) * step;
    for (let y = y0; y <= v.y1; y += step) {
      const a = cam.project(v.x0, y), b = cam.project(v.x1, y);
      ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    }
    ctx.stroke();
  }

  private drawNanolathe(ctx: CanvasRenderingContext2D, alpha: number): void {
    const cam = this.cam;
    const w = this.sim.world;
    const t = performance.now() * 0.001;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.kind[e] !== KIND_UNIT) continue;
      const act = w.action[e];
      if (act !== ACT_BUILD && act !== ACT_RECLAIM && act !== ACT_REPAIR) continue;
      const tgt = w.buildTargetEnt[e];
      if (tgt < 0 || !w.isAlive(tgt)) continue;
      const reclaim = act === ACT_RECLAIM;
      const bp = cam.project(lerp(w.prevX[e], w.posX[e], alpha), lerp(w.prevY[e], w.posY[e], alpha));
      const gp = cam.project(w.posX[tgt], w.posY[tgt]);
      const c = reclaim ? "#ffc24d" : act === ACT_REPAIR ? "#7cf0d0" : this.ownerColor(w.owner[e]);
      ctx.strokeStyle = c;
      ctx.globalAlpha = 0.35 + 0.15 * Math.sin(t * 20);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.lineDashOffset = -t * 30;
      ctx.beginPath(); ctx.moveTo(bp.x, bp.y); ctx.lineTo(gp.x, gp.y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 0.8;
      ctx.fillStyle = c;
      for (let k = 0; k < 3; k++) {
        const f = ((t * 2 + k * 0.33) % 1);
        ctx.beginPath(); ctx.arc(lerp(bp.x, gp.x, f), lerp(bp.y, gp.y, f), 1.6, 0, 7); ctx.fill();
      }
      const gr = (w.def(tgt).radius + 2) * cam.zoom;
      ctx.globalAlpha = 0.25 + 0.2 * Math.sin(t * 12);
      ctx.strokeStyle = c;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(gp.x, gp.y, gr, 0, 7); ctx.stroke();
      ctx.globalAlpha = 1;
    }
  }

  private drawRallyLines(ctx: CanvasRenderingContext2D, view: RenderView): void {
    const cam = this.cam;
    const w = this.sim.world;
    for (const e of view.selection) {
      if (!w.isAlive(e) || w.def(e).role !== "factory") continue;
      const rx = w.rallyX[e], ry = w.rallyY[e];
      if (Math.abs(rx - w.posX[e]) < 2 && Math.abs(ry - w.posY[e]) < 2) continue;
      const a = cam.project(w.posX[e], w.posY[e]);
      const b = cam.project(rx, ry);
      ctx.strokeStyle = "rgba(130,240,175,0.6)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 5]);
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "rgba(130,240,175,0.8)";
      ctx.beginPath(); ctx.arc(b.x, b.y, 3.5, 0, 7); ctx.fill();
    }
  }

  private drawPlacement(ctx: CanvasRenderingContext2D, placing: NonNullable<RenderView["placing"]>): void {
    const cam = this.cam;
    const w = this.sim.world;
    const def = w.def(w.defIndex(placing.defId));
    const legal = canPlace(w, placing.defId, placing.x, placing.y);
    const p = cam.project(placing.x, placing.y);
    const sx = p.x, sy = p.y;
    const z = cam.zoom;
    const snap = placing.snap;
    if (snap && legal) {
      if (snap.magnetized) {
        const np = cam.project(snap.nx, snap.ny);
        ctx.strokeStyle = "rgba(124,240,208,0.9)";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(np.x, np.y); ctx.lineTo(sx, sy); ctx.stroke();
        ctx.setLineDash([]);
        const dx = sx - np.x, dy = sy - np.y;
        const mm = Math.hypot(dx, dy) || 1;
        const tx = -dy / mm, ty = dx / mm;
        const midx = (sx + np.x) / 2, midy = (sy + np.y) / 2;
        const half = (def.radius + 2) * z;
        ctx.strokeStyle = "rgba(150,255,220,0.95)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(midx - tx * half, midy - ty * half);
        ctx.lineTo(midx + tx * half, midy + ty * half);
        ctx.stroke();
      } else {
        const r = (def.radius + 8) * z;
        ctx.strokeStyle = "rgba(124,240,208,0.35)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx - r, sy); ctx.lineTo(sx + r, sy);
        ctx.moveTo(sx, sy - r); ctx.lineTo(sx, sy + r);
        ctx.stroke();
      }
    }
    ctx.fillStyle = legal ? "rgba(120,230,160,0.15)" : "rgba(255,80,60,0.15)";
    ctx.beginPath(); ctx.arc(sx, sy, def.radius * z, 0, 7); ctx.fill();
    ctx.strokeStyle = legal ? "rgba(120,230,160,0.95)" : "rgba(255,80,60,0.95)";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(sx, sy, (def.radius + 3) * z, 0, 7); ctx.stroke();
    if (!legal) {
      ctx.beginPath();
      ctx.moveTo(sx - def.radius * z, sy - def.radius * z);
      ctx.lineTo(sx + def.radius * z, sy + def.radius * z);
      ctx.stroke();
    }
    if (def.weapon) {
      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(sx, sy, def.weapon.range * z, 0, 7); ctx.stroke();
    }
  }

  private drawSelBox(ctx: CanvasRenderingContext2D, b: NonNullable<RenderView["selBox"]>): void {
    ctx.strokeStyle = "rgba(130,240,175,0.95)";
    ctx.fillStyle = "rgba(120,230,160,0.10)";
    ctx.lineWidth = 1.5;
    const x = Math.min(b.x0, b.x1), y = Math.min(b.y0, b.y1);
    const bw = Math.abs(b.x1 - b.x0), bh = Math.abs(b.y1 - b.y0);
    ctx.fillRect(x, y, bw, bh);
    ctx.strokeRect(x, y, bw, bh);
  }

  private drawVignette(ctx: CanvasRenderingContext2D): void {
    const cam = this.cam;
    const g = ctx.createRadialGradient(
      cam.screenW / 2, cam.screenH / 2, Math.min(cam.screenW, cam.screenH) * 0.42,
      cam.screenW / 2, cam.screenH / 2, Math.max(cam.screenW, cam.screenH) * 0.75,
    );
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, "rgba(0,0,0,0.42)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cam.screenW, cam.screenH);
  }
}
